/**
 * Catalogue d'apparence du bonhomme.
 *
 * Ce fichier ne depend que des types partages: pas de three.js, pas de DOM.
 * Le rendu (client/character/bonhomme.ts) lit les index et construit la
 * geometrie; le createur de personnage (client/ui/creation.ts) lit CATALOGUE
 * pour remplir ses onglets. Les `nom` sont du francais accentue destine a
 * l'affichage.
 */
import type { Apparence, Corps } from '../types/player.ts'
import type { Alea } from '../sim/alea.ts'
import { clamp, melangerCouleur } from '../sim/alea.ts'

export interface OptionApparence {
  /** Index de l'option dans son tableau. Stable: c'est ce qui est sauvegarde. */
  id: number
  nom: string
  couleurDefaut: number
}

export type CategorieApparence =
  | 'cheveux'
  | 'yeux'
  | 'barbe'
  | 'chapeau'
  | 'haut'
  | 'bas'
  | 'souliers'
  | 'accessoire'

// ---------------------------------------------------------------- palettes

/** Teintes de peau, du plus clair au plus fonce. */
export const PALETTE_PEAU: readonly number[] = Object.freeze([
  0xfbe0c4, 0xf5cfa4, 0xecb789, 0xd89a63, 0xb87a4b, 0x94603a, 0x6d4227, 0x472c1b,
  0xffeedd, 0xe8b98f, 0xc98a58, 0x7f4f2e,
])

/** Couleurs de cheveux: naturelles d'abord, fantaisie a la fin. */
export const PALETTE_CHEVEUX: readonly number[] = Object.freeze([
  0x1b1512, 0x3a2a1e, 0x6b4a2f, 0x9c6c3f, 0xd3a44f, 0xf0e0a6, 0xb5482a, 0xdcdcdc, 0x3fa3d6, 0xd455a6,
  0x8c8c94, 0x5a3f6b, 0x2f7f5e,
])

/** Couleurs de vetements: franches, lisibles vue de dessus a 45 degres. */
export const PALETTE_VETEMENT: readonly number[] = Object.freeze([
  0xe23b3b, 0xf07f1a, 0xf5c518, 0x8dc63f, 0x2fa34a, 0x1fa89b, 0x2b7fd4, 0x2a3f9e, 0x7a4fd0, 0xd44f9e,
  0xf2a5b6, 0x8b5a2b, 0xd9cbb2, 0xf2f2f2, 0x8a8f98, 0x2b2f36,
])

/** Couleurs d'iris credibles. */
export const PALETTE_YEUX: readonly number[] = Object.freeze([
  0x3a2a1e, 0x6b4a2f, 0x2f6f4f, 0x3a7ea8, 0x6e7f8a, 0x1b1512, 0x8a6a3c, 0x4f5f7a,
])

// ---------------------------------------------------------------- catalogue

function opts(entrees: readonly (readonly [string, number])[]): readonly OptionApparence[] {
  return Object.freeze(
    entrees.map((e, i) => Object.freeze({ id: i, nom: e[0], couleurDefaut: e[1] })),
  )
}

/**
 * Toutes les options, par categorie. L'index 0 est toujours l'option "vide"
 * quand la categorie en a une (chauve, aucune barbe, aucun chapeau, aucun
 * accessoire, pieds nus).
 */
export const CATALOGUE: Readonly<Record<CategorieApparence, readonly OptionApparence[]>> =
  Object.freeze({
    cheveux: opts([
      ['Chauve', 0x3a2a1e],
      ['Courts', 0x3a2a1e],
      ['Ondulés', 0x6b4a2f],
      ['Queue de cheval', 0xd3a44f],
      ['Chignon', 0x1b1512],
      ['Longs', 0x9c6c3f],
      ['Nattes', 0xb5482a],
      ['Crête', 0x3fa3d6],
      ['Afro', 0x1b1512],
      ['Carré', 0x3a2a1e],
    ]),
    yeux: opts([
      ['Ronds', 0x3a2a1e],
      ['En amande', 0x6b4a2f],
      ['Rieurs', 0x2f6f4f],
      ['Grands', 0x3a7ea8],
      ['Endormis', 0x6e7f8a],
      ['Malicieux', 0x1b1512],
    ]),
    barbe: opts([
      ['Aucune', 0x3a2a1e],
      ['Moustache', 0x3a2a1e],
      ['Bouc', 0x1b1512],
      ['Barbe courte', 0x6b4a2f],
      ['Grande barbe', 0xdcdcdc],
    ]),
    chapeau: opts([
      ['Aucun', 0xe23b3b],
      ['Tuque', 0xe23b3b],
      ['Casquette', 0x2b7fd4],
      ['Chapeau de paille', 0xd9cbb2],
      ['Chapeau de cowboy', 0x8b5a2b],
      ['Casque de chantier', 0xf5c518],
      ['Couronne', 0xf5c518],
      ['Bandeau', 0x2fa34a],
      ['Béret', 0xe23b3b],
    ]),
    haut: opts([
      ['Camisole', 0xf2f2f2],
      ['Tricot simple', 0x2b7fd4],
      ['Chemise à carreaux', 0xe23b3b],
      ['Chandail à capuchon', 0x8a8f98],
      ['Salopette', 0x2a3f9e],
      ['Veste de travail', 0x8b5a2b],
      ['Tunique', 0x2fa34a],
      ['Manteau d’hiver', 0x1fa89b],
      ['Chandail rayé', 0xf07f1a],
      ['Blouse fleurie', 0xf2a5b6],
      ['Chemisier ouvert', 0xd9cbb2],
    ]),
    bas: opts([
      ['Pantalon', 0x2a3f9e],
      ['Jeans', 0x2b7fd4],
      ['Culottes courtes', 0x8dc63f],
      ['Pantalon de travail', 0x8b5a2b],
      ['Jupe', 0xd44f9e],
      ['Robe d’été', 0xf5c518],
      ['Collants', 0x2b2f36],
      ['Bermuda fleuri', 0x1fa89b],
    ]),
    souliers: opts([
      ['Pieds nus', 0xd89a63],
      ['Souliers', 0x2b2f36],
      ['Espadrilles', 0xf2f2f2],
      ['Bottes de pluie', 0xf5c518],
      ['Bottes de travail', 0x8b5a2b],
      ['Sandales', 0x8b5a2b],
    ]),
    accessoire: opts([
      ['Aucun', 0x8b5a2b],
      ['Sac à dos', 0x2fa34a],
      ['Lunettes', 0x2b2f36],
      ['Écharpe', 0xe23b3b],
      ['Cape', 0x7a4fd0],
      ['Ceinture à outils', 0x8b5a2b],
    ]),
  })

export const NOMS_CORPS: Record<Corps, string> = Object.freeze({
  homme: 'Homme',
  femme: 'Femme',
  neutre: 'Neutre',
})

/** Les trois morphologies, dans l'ordre d'affichage. */
export const CORPS: readonly Corps[] = Object.freeze(['homme', 'femme', 'neutre'] as Corps[])

// ------------------------------------------------------------------ defauts

export function apparenceParDefaut(): Apparence {
  return {
    corps: 'neutre',
    peau: PALETTE_PEAU[2] as number,
    cheveux: 1,
    cheveuxCouleur: PALETTE_CHEVEUX[2] as number,
    yeux: 0,
    yeuxCouleur: PALETTE_YEUX[0] as number,
    barbe: 0,
    barbeCouleur: PALETTE_CHEVEUX[2] as number,
    chapeau: 0,
    chapeauCouleur: PALETTE_VETEMENT[0] as number,
    haut: 1,
    hautCouleur: PALETTE_VETEMENT[6] as number,
    bas: 0,
    basCouleur: PALETTE_VETEMENT[7] as number,
    souliers: 1,
    souliersCouleur: PALETTE_VETEMENT[15] as number,
    accessoire: 0,
    accessoireCouleur: PALETTE_VETEMENT[4] as number,
  }
}

// ----------------------------------------------------------------- aleatoire

/** Vetements du bas qui couvrent aussi le haut du corps (une robe se porte seule). */
const BAS_ROBE = 5

/** Hauts feminins ou unisexes, pour eviter les combinaisons bizarres. */
const HAUTS_AVEC_ROBE: readonly number[] = [0, 6, 9]

/**
 * Une apparence plaisante. Les couleurs sont tirees dans PALETTE_VETEMENT,
 * mais une fois sur deux on force une harmonie: la piece du bas reprend la
 * teinte du haut assombrie, ce qui evite les arlequins.
 */
export function apparenceAleatoire(alea: Alea): Apparence {
  const corps: Corps = alea.choix(CORPS)
  const peau = alea.choix(PALETTE_PEAU)
  const cheveuxCouleur = alea.choix(PALETTE_CHEVEUX)

  const cheveux = alea.chance(0.08) ? 0 : alea.entier(1, CATALOGUE.cheveux.length - 1)
  // Une barbe sur un corps 'femme' reste possible mais rare.
  const probaBarbe = corps === 'homme' ? 0.55 : corps === 'neutre' ? 0.25 : 0.04
  const barbe = alea.chance(probaBarbe) ? alea.entier(1, CATALOGUE.barbe.length - 1) : 0

  const bas = alea.entier(0, CATALOGUE.bas.length - 1)
  const haut =
    bas === BAS_ROBE ? alea.choix(HAUTS_AVEC_ROBE) : alea.entier(0, CATALOGUE.haut.length - 1)

  const hautCouleur = alea.choix(PALETTE_VETEMENT)
  const harmonie = alea.chance(0.5)
  const basCouleur = harmonie
    ? melangerCouleur(hautCouleur, 0x1b1f26, alea.entre(0.35, 0.6))
    : alea.choix(PALETTE_VETEMENT)

  const chapeau = alea.chance(0.45) ? alea.entier(1, CATALOGUE.chapeau.length - 1) : 0
  const accessoire = alea.chance(0.4) ? alea.entier(1, CATALOGUE.accessoire.length - 1) : 0

  return {
    corps,
    peau,
    cheveux,
    cheveuxCouleur,
    yeux: alea.entier(0, CATALOGUE.yeux.length - 1),
    yeuxCouleur: alea.choix(PALETTE_YEUX),
    barbe,
    // La barbe suit toujours les cheveux, sinon c'est un deguisement.
    barbeCouleur: cheveux === 0 ? cheveuxCouleur : melangerCouleur(cheveuxCouleur, 0x000000, 0.1),
    chapeau,
    chapeauCouleur: harmonie
      ? melangerCouleur(hautCouleur, 0xffffff, alea.entre(0.15, 0.35))
      : alea.choix(PALETTE_VETEMENT),
    haut,
    hautCouleur,
    bas,
    basCouleur,
    souliers: alea.entier(0, CATALOGUE.souliers.length - 1),
    souliersCouleur: alea.chance(0.6)
      ? melangerCouleur(basCouleur, 0x14161a, 0.5)
      : alea.choix(PALETTE_VETEMENT),
    accessoire,
    accessoireCouleur: alea.choix(PALETTE_VETEMENT),
  }
}

// ---------------------------------------------------------------- validation

function borneIndex(v: unknown, n: number): number {
  const x = typeof v === 'number' && Number.isFinite(v) ? Math.floor(v) : 0
  return clamp(x, 0, n - 1)
}

function borneCouleur(v: unknown): number {
  const x = typeof v === 'number' && Number.isFinite(v) ? Math.floor(v) : 0
  return clamp(x, 0, 0xffffff)
}

function borneCorps(v: unknown): Corps {
  return v === 'homme' || v === 'femme' || v === 'neutre' ? v : 'neutre'
}

/**
 * Rend une Apparence saine a partir de n'importe quoi: sauvegarde d'une
 * ancienne version, JSON tronque, valeurs hors bornes. Ne jette jamais.
 */
export function validerApparence(a: unknown): Apparence {
  const defaut = apparenceParDefaut()
  if (typeof a !== 'object' || a === null) return defaut
  const o = a as Record<string, unknown>
  const peau = borneCouleur(o['peau'] ?? defaut.peau)
  return {
    corps: borneCorps(o['corps']),
    peau,
    cheveux: borneIndex(o['cheveux'] ?? defaut.cheveux, CATALOGUE.cheveux.length),
    cheveuxCouleur: borneCouleur(o['cheveuxCouleur'] ?? defaut.cheveuxCouleur),
    yeux: borneIndex(o['yeux'] ?? defaut.yeux, CATALOGUE.yeux.length),
    yeuxCouleur: borneCouleur(o['yeuxCouleur'] ?? defaut.yeuxCouleur),
    barbe: borneIndex(o['barbe'] ?? defaut.barbe, CATALOGUE.barbe.length),
    barbeCouleur: borneCouleur(o['barbeCouleur'] ?? defaut.barbeCouleur),
    chapeau: borneIndex(o['chapeau'] ?? defaut.chapeau, CATALOGUE.chapeau.length),
    chapeauCouleur: borneCouleur(o['chapeauCouleur'] ?? defaut.chapeauCouleur),
    haut: borneIndex(o['haut'] ?? defaut.haut, CATALOGUE.haut.length),
    hautCouleur: borneCouleur(o['hautCouleur'] ?? defaut.hautCouleur),
    bas: borneIndex(o['bas'] ?? defaut.bas, CATALOGUE.bas.length),
    basCouleur: borneCouleur(o['basCouleur'] ?? defaut.basCouleur),
    souliers: borneIndex(o['souliers'] ?? defaut.souliers, CATALOGUE.souliers.length),
    souliersCouleur: borneCouleur(o['souliersCouleur'] ?? defaut.souliersCouleur),
    accessoire: borneIndex(o['accessoire'] ?? defaut.accessoire, CATALOGUE.accessoire.length),
    accessoireCouleur: borneCouleur(o['accessoireCouleur'] ?? defaut.accessoireCouleur),
  }
}
